/**
 * Trending Strains — sliver between Live Menu Preview and Stores.
 * Pulls the top N strains by product_count from the live strain library.
 *
 * SEO bonus: each tile is a Link that internal-links the strain detail
 * pages directly from the home page, lifting their crawl frequency.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Leaf, ArrowUpRight } from "lucide-react";
import { getStrainLibrary } from "../../lib/api";

const STRAIN_TYPE_DOT = {
    INDICA: { tone: "bg-purple-500/80", label: "Indica" },
    SATIVA: { tone: "bg-amber-500/80", label: "Sativa" },
    HYBRID: { tone: "bg-emerald-500/80", label: "Hybrid" },
    INDICA_HYBRID: { tone: "bg-purple-500/80", label: "Indica-Hybrid" },
    SATIVA_HYBRID: { tone: "bg-amber-500/80", label: "Sativa-Hybrid" },
    HIGH_CBD: { tone: "bg-sky-500/80", label: "High CBD" },
};

const STORE = "queens-long-island";
const TILES = 8;

export default function HomeTrendingStrains() {
    const [strains, setStrains] = useState(null);

    useEffect(() => {
        let cancelled = false;
        getStrainLibrary(STORE)
            .then((d) => !cancelled && setStrains(d?.strains || []))
            .catch(() => !cancelled && setStrains([]));
        return () => {
            cancelled = true;
        };
    }, []);

    const top = (strains || []).slice(0, TILES);

    if (strains !== null && top.length === 0) return null;

    return (
        <section
            data-testid="home-trending-strains"
            className="relative py-12 sm:py-16 px-6 sm:px-10 bg-zen-surface border-t border-zen-ink/8"
        >
            <div className="max-w-[1400px] mx-auto">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8">
                    <div>
                        <p className="text-[11px] uppercase tracking-[0.35em] text-zen-olive mb-4 inline-flex items-center gap-2">
                            <Leaf size={11} /> Trending strains
                        </p>
                        <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light text-zen-ink leading-[1.05] max-w-2xl">
                            What New York is{" "}
                            <span className="italic text-zen-olive">smoking now.</span>
                        </h2>
                        <p className="text-zen-ink-soft mt-4 max-w-xl leading-relaxed">
                            Ranked by live stock across our two NY flagships. Every
                            tile links straight to that strain&rsquo;s shop page.
                        </p>
                    </div>
                    <Link
                        to={`/strains/${STORE}`}
                        data-testid="home-strain-library-link"
                        className="zen-link text-[12px] uppercase tracking-[0.25em] text-zen-ink-soft hover:text-zen-ink inline-flex items-center gap-2 shrink-0"
                    >
                        Browse the full strain library <ArrowUpRight size={14} />
                    </Link>
                </div>

                {strains === null ? (
                    <p className="text-sm text-zen-ink-soft py-8">
                        Loading trending strains&hellip;
                    </p>
                ) : (
                    <div
                        className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-8 gap-2 sm:gap-3"
                        data-testid="home-trending-strain-grid"
                    >
                        {top.map((s) => {
                            const tone =
                                STRAIN_TYPE_DOT[s.strain_type] || STRAIN_TYPE_DOT.HYBRID;
                            return (
                                <Link
                                    key={s.slug}
                                    to={`/menu/${STORE}/strain/${s.slug}`}
                                    data-testid={`home-trending-strain-${s.slug}`}
                                    className="group relative aspect-square bg-zen-bg border border-zen-ink/10 hover:border-zen-olive hover:-translate-y-0.5 transition-all duration-500 p-4 sm:p-5 flex flex-col justify-between overflow-hidden"
                                >
                                    <div className="flex items-start justify-between">
                                        <span
                                            className={`w-2 h-2 rounded-full ${tone.tone}`}
                                            aria-hidden="true"
                                        />
                                        <span className="text-[10px] uppercase tracking-[0.2em] text-zen-ink-soft tabular-nums">
                                            {s.product_count}
                                        </span>
                                    </div>
                                    <div>
                                        <p className="text-[10px] uppercase tracking-[0.2em] text-zen-ink-soft mb-1.5">
                                            {tone.label}
                                        </p>
                                        <h3 className="font-serif text-lg sm:text-xl text-zen-ink font-light leading-tight tracking-tight group-hover:text-zen-olive transition-colors">
                                            {s.name}
                                        </h3>
                                    </div>
                                </Link>
                            );
                        })}
                    </div>
                )}
            </div>
        </section>
    );
}
